// API service for admin portal
import axios, { AxiosInstance } from 'axios';
import {
  AdminJob,
  AdminResource,
  DashboardMetrics,
  JobSearchFilters,
  BackgroundTask,
  ApiResponse,
  PaginatedResponse,
  RetryTaskRequest,
  GenerateTokenRequest,
  Assigner,
  AssignerDetail,
  DirectoryUser,
  AdminReporter,
  StatePreference,
} from '../types';

class AdminApiService {
  private api: AxiosInstance;
  private tokenProvider: (() => Promise<string | null>) | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL || '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(async (config) => {
      try {
        // Get fresh token from provider (which handles refresh automatically)
        let token: string | null = null;
        
        if (this.tokenProvider) {
          token = await this.tokenProvider();
          console.log('API Request - Token from provider:', !!token);
        } else {
          token = localStorage.getItem('adminToken');
          console.log('API Request - Token from localStorage:', !!token);
        }
        
        console.log('API Request - Token starts with:', token?.substring(0, 20) + '...');
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('API Request - Authorization header set');
        } else {
          console.log('API Request - No token available, rejecting request');
          // If no token is present, reject the request to prevent 401 loops
          return Promise.reject(new Error('No authentication token available'));
        }
        return config;
      } catch (error) {
        console.error('Failed to get token for request:', error);
        return Promise.reject(new Error('Failed to authenticate request'));
      }
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          console.log('API Response - 401 error received');
          
          // Clear stored token as it's invalid
          localStorage.removeItem('adminToken');
          
          // The AuthProvider will detect the failed token refresh and update authentication state
          // which will trigger route protection and redirect to login automatically
        }
        return Promise.reject(error);
      }
    );
  }

  // Set token provider - to be called from AuthProvider
  setTokenProvider(provider: () => Promise<string | null>) {
    this.tokenProvider = provider;
  }

  // Authentication
  async login(email: string, password: string): Promise<ApiResponse<{ token: string; user: any }>> {
    const response = await this.api.post('/admin/auth/login', { email, password });
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/admin/auth/logout');
    } finally {
      localStorage.removeItem('adminToken');
    }
  }

  async getCurrentUser(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/admin/auth/me');
    return response.data;
  }

  // Dashboard
  async getDashboardMetrics(): Promise<ApiResponse<DashboardMetrics>> {
    const response = await this.api.get('/admin/dashboard/metrics');
    return response.data;
  }

  // Jobs
  async getJobs(
    page: number = 1,
    pageSize: number = 25,
    filters?: JobSearchFilters
  ): Promise<PaginatedResponse<AdminJob>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (filters) {
      if (filters.workflowState?.length) {
        params.append('workflowState', filters.workflowState.join(','));
      }
      if (filters.completionStatus?.length) {
        params.append('completionStatus', filters.completionStatus.join(','));
      }
      if (filters.dateRange?.startDate) {
        params.append('startDate', filters.dateRange.startDate);
      }
      if (filters.dateRange?.endDate) {
        params.append('endDate', filters.dateRange.endDate);
      }
      if (filters.reporterName) {
        params.append('reporterName', filters.reporterName);
      }
      if (filters.jobNumber) {
        params.append('jobNumber', filters.jobNumber);
      }
      if (filters.caseName) {
        params.append('caseName', filters.caseName);
      }
      if (filters.firmName) {
        params.append('firmName', filters.firmName);
      }
    }

    const url = `/admin/jobs?${params.toString()}`;
    console.log('API request URL:', url);
    console.log('Filters being sent:', filters);
    const response = await this.api.get(url);
    return response.data;
  }

  async getJob(jobNumber: string): Promise<ApiResponse<AdminJob>> {
    const response = await this.api.get(`/admin/jobs/${jobNumber}`);
    return response.data;
  }

  async getJobFiles(jobNumber: string): Promise<{ success: boolean; files: any[]; worksheetFiles: any[] }> {
    const response = await this.api.get(`/admin/jobs/${jobNumber}/files`);
    return response.data;
  }

  async getJobWorksheet(jobNumber: string, fileName: string): Promise<any> {
    const response = await this.api.get(`/admin/jobs/${jobNumber}/worksheet/${fileName}`);
    return response.data;
  }

  // Background tasks
  async getBackgroundTasks(
    jobNumber?: string,
    status?: string,
    page: number = 1,
    pageSize: number = 25
  ): Promise<PaginatedResponse<BackgroundTask>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (jobNumber) params.append('jobNumber', jobNumber);
    if (status) params.append('status', status);

    const response = await this.api.get(`/admin/background-tasks?${params.toString()}`);
    return response.data;
  }

  async retryTask(request: RetryTaskRequest): Promise<ApiResponse<any>> {
    const response = await this.api.post('/admin/background-tasks/retry', request);
    return response.data;
  }

  // Token management
  async generateToken(request: GenerateTokenRequest): Promise<ApiResponse<{ token: string }>> {
    const response = await this.api.post('/admin/tokens/generate', request);
    return response.data;
  }

  async getTokens(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/admin/tokens');
    return response.data;
  }

  // Reports
  async getReporterStats(
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await this.api.get(`/admin/reports/reporters?${params.toString()}`);
    return response.data;
  }

  async getActivityLog(
    page: number = 1,
    pageSize: number = 25,
    jobNumber?: string
  ): Promise<PaginatedResponse<any>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (jobNumber) params.append('jobNumber', jobNumber);

    const response = await this.api.get(`/admin/activity-log?${params.toString()}`);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Scheduling
  async getSchedulingData(
    weekStart?: string,
    state?: string,
    primaryOnly: boolean = true
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (weekStart) params.append('weekStart', weekStart);
    if (state) params.append('state', state);
    params.append('primaryOnly', primaryOnly ? 'true' : 'false');

    const response = await this.api.get(`/admin/scheduling?${params.toString()}`);
    return response.data;
  }

  async getReporters(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get('/admin/reporters');
    return response.data;
  }

  // Resources
  async getResources(includeInactive: boolean = true): Promise<ApiResponse<AdminResource[]>> {
    const params = new URLSearchParams();
    params.append('includeInactive', includeInactive.toString());
    const response = await this.api.get(`/admin/resources?${params.toString()}`);
    return response.data;
  }

  async getResource(id: number): Promise<ApiResponse<AdminResource>> {
    const response = await this.api.get(`/admin/resources/${id}`);
    return response.data;
  }

  async createResource(formData: FormData): Promise<ApiResponse<AdminResource>> {
    const response = await this.api.post('/admin/resources', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateResource(id: number, data: {
    name?: string;
    description?: string;
    displayOrder?: number;
    isActive?: boolean;
  }): Promise<ApiResponse<AdminResource>> {
    const response = await this.api.put(`/admin/resources/${id}`, data);
    return response.data;
  }

  async deleteResource(id: number): Promise<ApiResponse<{ message: string }>> {
    const response = await this.api.delete(`/admin/resources/${id}`);
    return response.data;
  }

  async replaceResourceFile(id: number, file: File): Promise<ApiResponse<AdminResource>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(`/admin/resources/${id}/replace-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getResourceDownloadUrl(id: number): Promise<{ success: boolean; downloadUrl?: string; fileName?: string; mimeType?: string; error?: string }> {
    const response = await this.api.get(`/admin/resources/${id}/download`);
    return response.data;
  }

  // Assigners
  async getAssigners(includeInactive = false): Promise<ApiResponse<Assigner[]>> {
    const params = new URLSearchParams();
    if (includeInactive) params.append('includeInactive', 'true');
    const response = await this.api.get(`/admin/assigners?${params.toString()}`);
    return response.data;
  }

  async getMyAssigner(): Promise<ApiResponse<Assigner | null>> {
    const response = await this.api.get('/admin/assigners/me');
    return response.data;
  }

  async getAssigner(id: number): Promise<ApiResponse<AssignerDetail>> {
    const response = await this.api.get(`/admin/assigners/${id}`);
    return response.data;
  }

  async createAssigner(data: { azureUserId: string; email: string; name: string }): Promise<ApiResponse<Assigner>> {
    const response = await this.api.post('/admin/assigners', data);
    return response.data;
  }

  async deactivateAssigner(id: number): Promise<ApiResponse<{ message: string }>> {
    const response = await this.api.delete(`/admin/assigners/${id}`);
    return response.data;
  }

  async setAssignerStates(assignerId: number, states: string[]): Promise<ApiResponse<Assigner>> {
    const response = await this.api.put(`/admin/assigners/${assignerId}/states`, { states });
    return response.data;
  }

  // Directory search
  async searchDirectory(query: string): Promise<ApiResponse<DirectoryUser[]>> {
    const response = await this.api.get(`/admin/directory/search?q=${encodeURIComponent(query)}`);
    return response.data;
  }

  // Reporters list for admin Reporters page
  async getAdminReporters(): Promise<ApiResponse<AdminReporter[]>> {
    const response = await this.api.get('/admin/reporters/all');
    return response.data;
  }

  // Reporter preferences (admin override)
  async getReporterPreferences(reporterId: string): Promise<ApiResponse<{ reporterId: string; statePreferences: StatePreference[] }>> {
    const response = await this.api.get(`/admin/reporters/${reporterId}/preferences`);
    return response.data;
  }

  async setReporterPreferences(reporterId: string, statePreferences: StatePreference[]): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/admin/reporters/${reporterId}/preferences`, { statePreferences });
    return response.data;
  }

  // Admin availability editing
  async setReporterAvailability(data: {
    reporterId: string;
    availabilityDate: string;
    hourSlots: string;
    notes?: string;
    unavailable?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/admin/scheduling/availability', data);
    return response.data;
  }

  async removeReporterAvailability(reporterId: string, availabilityDate: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/admin/scheduling/availability?reporterId=${reporterId}&availabilityDate=${availabilityDate}`);
    return response.data;
  }

  // Agency management (admin)
  async getAgencyStatus(reporterId: string): Promise<ApiResponse<{ isAgency: boolean; authorizedEmails?: { id: number; email: string; createdAt: string; createdBy: string }[] }>> {
    const response = await this.api.get(`/admin/reporters/${reporterId}/agency`);
    return response.data;
  }

  async setAgencyFlag(reporterId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/admin/reporters/${reporterId}/agency`);
    return response.data;
  }

  async removeAgencyFlag(reporterId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/admin/reporters/${reporterId}/agency`);
    return response.data;
  }

  async addAgencyEmail(reporterId: string, email: string): Promise<ApiResponse<{ warning?: string }>> {
    const response = await this.api.post(`/admin/reporters/${reporterId}/agency/emails`, { email });
    return response.data;
  }

  async removeAgencyEmail(reporterId: string, email: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/admin/reporters/${reporterId}/agency/emails/${encodeURIComponent(email)}`);
    return response.data;
  }
}

export const adminApiService = new AdminApiService();
export default adminApiService;